import { useEffect, useState, useRef, useCallback } from "react";

import {
  generateQRWithLogo as generateQRWithLogoUtil,
  getQRFallbackUrl,
} from "@/shared/utils/generateQRWithLogo";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { DocumentType, DocumentMetadata, UserDocument } from "@/shared/types/documentTypes";
import { enqueueSnackbar } from "notistack";
import { UploadDocumentsForm } from "./UploadDocumentsForm";
import { DocumentFormInput, StagedDocument } from "@/shared/hooks/useDocumentUpload";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { UseFormRegister } from "react-hook-form";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAuth } from "@/shared/hooks/useAuth";
import { UploadedDocument } from "./UploadedDocument";
import Loader from "@/shared/components/Loader";

interface UploadDocumentsDialogProps {
  isOpen: boolean;
  onClose: (val: boolean) => void;
  onComplete?: (uploadedDocuments?: DocumentMetadata[]) => void;
  documentTypes?: { id: DocumentType; label: string }[];
  header?: string;
  subheader?: string;
  existingDocuments?: DocumentMetadata[];
  disableParsingAnimation?: boolean;
  // Action Center integration props
  postLossTaskId?: number;
  stepLabel?: string;
  autoSaveCallback?: (documentIds: number[]) => void;
  existingDocumentIds?: number[];
}

import { FREE_DOCUMENT_LIMIT } from "@/shared/hooks/useUploadPaywall";

export interface IFormInput {
  documents: UserDocument[];
  documentTypes: { documentType: DocumentType }[];
}

export default function UploadDocumentsDialog({
  isOpen,
  onClose,
  onComplete,
  documentTypes = [],
  header = "Add documents",
  disableParsingAnimation = false,
  postLossTaskId,
  stepLabel,
  autoSaveCallback,
  existingDocumentIds = [],
}: UploadDocumentsDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const formRef = useRef<{
    submitForm: () => void;
    isSubmitting: boolean;
    stagedDocumentsCount: number;
    errorIndexes: number[];
    stageRemoteDocument: (remote: {
      fileKey: string;
      fileName: string;
      contentType?: string;
      documentType?: DocumentType;
    }) => void;
    getStagedDocuments: () => StagedDocument[];
    handleDocumentTypeChange: (v: DocumentType, ix: number) => void;
    handleDocumentRemove: (ix: number) => void;
  }>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [mobileUrl, setMobileUrl] = useState<string>("");
  const { person, user } = useAppContext();
  const { getAccessToken } = useAuth();
  const [activeTab, setActiveTab] = useState<string>("computer");
  const [stagedDocs, setStagedDocs] = useState<StagedDocument[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");

  useEffect(() => {
    if (!mobileUrl || activeTab !== "scan") return;
    let cancelled = false;

    generateQRWithLogoUtil(mobileUrl)
      .then((dataUrl) => {
        if (!cancelled) setQrCodeDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrCodeDataUrl(getQRFallbackUrl(mobileUrl));
      });

    return () => {
      cancelled = true;
    };
  }, [mobileUrl, activeTab]);

  // Create session when switching to scan tab
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only create session if dialog is open, on scan tab, and no session exists
      if (!isOpen || activeTab !== "scan" || sessionId) return;
      try {
        // Get access token first
        const at = await getAccessToken?.();

        // Create an upload session record (expires in 30 minutes) with stored access token
        const expiresAt = new Date(Date.now() + 30 * 60 * 1000).toISOString();
        const { data, error } = await supabase
          .from("upload_sessions")
          .insert({
            estate_id: person?.estate_id || null,
            created_by_user_id: user?.user_id || null,
            status: "active",
            expires_at: expiresAt,
            access_token: at || null,
          })
          .select("session_id")
          .single();
        if (error) throw error;
        if (cancelled) return;
        const sid = String(data.session_id);
        setSessionId(sid);

        // Create short URL with only session ID
        const origin = window.location.origin;
        const shortUrl = `${origin}/mobile-upload?sid=${sid}`;
        setMobileUrl(shortUrl);
      } catch (e) {
        // Non-blocking; user can still upload from computer
        console.error("Failed to initialize mobile upload session", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeTab, sessionId, getAccessToken, person?.estate_id, user?.user_id]);

  // Subscribe to realtime channel when session is created
  useEffect(() => {
    if (!sessionId) return;

    const channel = supabase.channel(`mobile_upload:${sessionId}`);

    channel.on(
      "broadcast",
      { event: "file_uploaded" },
      (payload: {
        payload: {
          fileKey: string;
          fileName: string;
          contentType?: string;
        };
      }) => {
        formRef.current?.stageRemoteDocument({
          fileKey: payload?.payload?.fileKey,
          fileName: payload?.payload?.fileName,
          contentType: payload?.payload?.contentType,
        });
        // Update staged docs list for Scan tab
        setTimeout(() => {
          const docs = formRef.current?.getStagedDocuments?.() || [];
          setStagedDocs(docs);
        }, 100);
      },
    );

    channel.subscribe();
    channelRef.current = channel;

    return () => {
      channel?.unsubscribe();
      channelRef.current = null;
    };
  }, [sessionId]);

  // Convert document types to the format expected by UploadDocumentsForm
  const documentSelectionTypes = documentTypes.map((type) => ({
    id: type.id,
    label: type.label,
  }));

  // Clean up session when dialog closes
  useEffect(() => {
    if (!isOpen && sessionId) {
      // Delete session from database
      (async () => {
        try {
          await supabase.from("upload_sessions").delete().eq("session_id", Number(sessionId));
        } catch (e) {
          console.error("Failed to delete upload session", e);
        }
      })();
      // Clear local state
      setSessionId("");
      setMobileUrl("");
      setStagedDocs([]);
    }
  }, [isOpen, sessionId]);

  // Handle dialog close
  const handleClose = useCallback(
    (open: boolean) => {
      onClose(open);
    },
    [onClose],
  );

  // Update local submitting state when form state changes
  const handleSubmit = () => {
    const stagedCount = formRef.current?.stagedDocumentsCount ?? 0;

    if (stagedCount === 0) {
      enqueueSnackbar("Please upload a document first", { variant: "warning" });
      return;
    }

    // Trigger form validation first
    formRef.current?.submitForm();

    // Check for validation errors after a brief delay to allow validation to run
    // Only validate document types if they were provided (Action Center flow)
    setTimeout(() => {
      const hasErrors = (formRef.current?.errorIndexes?.length ?? 0) > 0;
      if (hasErrors && documentTypes.length > 0) {
        // Don't show loader if there are validation errors (only for Action Center)
        enqueueSnackbar("Please select a document type for all documents", { variant: "warning" });
        return;
      }
      // Only show loader if validation passes
      setIsSubmitting(true);
    }, 100);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="md:w-[60%] md:max-h-[90%] max-w-none"
          aria-describedby="upload-dialog-description"
        >
          <DialogHeader className="flex-shrink-0">
            <div className="flex flex-row items-center gap-4">
              <DialogTitle>{header}</DialogTitle>
            </div>
          </DialogHeader>
          {/* Non-desktop: show existing content without tabs */}
          <DialogBody className="flex-1 min-h-0 overflow-y-auto px-4 py-6 md:px-[32px] md:py-[24px] lg:hidden">
            {isSubmitting ? (
              <div className="flex items-center justify-center min-h-[250px]">
                <Loader
                  size="lg"
                  label={`Uploading ${(formRef.current?.stagedDocumentsCount ?? 0) > 1 ? "documents" : "document"}...`}
                />
              </div>
            ) : (
              <UploadDocumentsForm
                ref={formRef}
                onCancel={() => {
                  setIsSubmitting(false);
                  handleClose(false);
                }}
                onSuccess={(uploadedDocuments: unknown) => {
                  setIsSubmitting(false);
                  onComplete?.(uploadedDocuments as DocumentMetadata[] | undefined);
                }}
                documentTypes={documentSelectionTypes}
                disableParsingAnimation={disableParsingAnimation}
                postLossTaskId={postLossTaskId}
                stepLabel={stepLabel}
                autoSaveCallback={autoSaveCallback}
                existingDocumentIds={existingDocumentIds}
              />
            )}
          </DialogBody>

          {/* Desktop (lg+): tabs */}
          <div className="hidden lg:flex lg:flex-col lg:flex-1 lg:min-h-0 lg:overflow-hidden">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="flex flex-col flex-1 min-h-0"
            >
              <TabsList
                className="h-[56px] px-4 md:px-[32px]"
                value={activeTab}
                onValueChange={setActiveTab}
              >
                <TabsTrigger value="computer">From your computer</TabsTrigger>
                <TabsTrigger value="scan" className="hidden md:inline-flex">
                  Scan with your phone
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="computer"
                className="flex-1 min-h-0 overflow-hidden flex flex-col px-4 py-6 md:px-[32px] md:py-[24px]"
                forceMount
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center min-h-[250px]">
                    <Loader
                      size="lg"
                      label={`Uploading ${(formRef.current?.stagedDocumentsCount ?? 0) > 1 ? "documents" : "document"}...`}
                    />
                  </div>
                ) : (
                  <UploadDocumentsForm
                    ref={formRef}
                    onCancel={() => {
                      setIsSubmitting(false);
                      handleClose(false);
                    }}
                    onSuccess={(uploadedDocuments: unknown) => {
                      setIsSubmitting(false);
                      onComplete?.(uploadedDocuments as DocumentMetadata[] | undefined);
                    }}
                    documentTypes={documentSelectionTypes}
                    disableParsingAnimation={disableParsingAnimation}
                    postLossTaskId={postLossTaskId}
                    stepLabel={stepLabel}
                    autoSaveCallback={autoSaveCallback}
                    existingDocumentIds={existingDocumentIds}
                    className="flex-1 min-h-0 [&>div]:flex-1 [&>div]:min-h-0 [&>div>div:last-child]:flex-1 [&>div>div:last-child]:max-h-none"
                  />
                )}
              </TabsContent>
              <TabsContent
                value="scan"
                className="flex-1 min-h-0 overflow-hidden px-4 py-6 md:px-[32px] md:py-[24px] text-sm text-stone-600"
                forceMount
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center min-h-[250px]">
                    <Loader
                      size="lg"
                      label={`Uploading ${(formRef.current?.stagedDocumentsCount ?? 0) > 1 ? "documents" : "document"}...`}
                    />
                  </div>
                ) : (
                  activeTab === "scan" && (
                    <div className="space-y-4">
                      {stagedDocs.length === 0 ? (
                        <div className="bg-stone-50 p-6 rounded-lg">
                          <div
                            className="grid gap-[42px] items-center"
                            style={{ gridTemplateColumns: "40% 60%" }}
                          >
                            <div>
                              <img
                                src={qrCodeDataUrl || getQRFallbackUrl(mobileUrl)}
                                alt="QR Code"
                                className="border-[32px] border-white w-full"
                              />
                            </div>
                            <div>
                              <p className="text-sm text-stone-700 font-semibold">
                                Scan the QR code to upload documents using your phone camera.
                              </p>
                              <p className="text-sm text-stone-700 mt-4">
                                1. Open your phone's camera app
                              </p>
                              <p className="text-sm text-stone-700">
                                2. Point the camera at the QR code
                              </p>
                              <p className="text-sm text-stone-700">
                                3. Tap the notification that appears
                              </p>
                              <p className="text-sm text-stone-700">
                                4. Follow the instructions to upload your files
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col h-full">
                          <p className="text-sm text-stone-500 mb-2 flex-shrink-0">
                            Added documents
                          </p>
                          <div className="flex-1 overflow-y-auto space-y-2">
                            {stagedDocs.map((d, idx) => {
                              const isErrored =
                                formRef.current?.errorIndexes?.includes(idx) || false;
                              return (
                                <UploadedDocument
                                  key={`${d.name}-${idx}`}
                                  documentTypes={documentSelectionTypes}
                                  document={d}
                                  register={
                                    ((fieldName: string) => ({
                                      onChange: async ({
                                        target: { value },
                                      }: {
                                        target: { value: DocumentType };
                                      }) => {
                                        formRef.current?.handleDocumentTypeChange(value, idx);
                                      },
                                      onBlur: async () => {},
                                      name: fieldName,
                                      ref: () => {},
                                    })) as unknown as UseFormRegister<DocumentFormInput>
                                  }
                                  ix={idx}
                                  onDocumentTypeChange={(v: DocumentType) => {
                                    formRef.current?.handleDocumentTypeChange(v, idx);
                                    // Update local staged list
                                    setTimeout(() => {
                                      const docs = formRef.current?.getStagedDocuments?.() || [];
                                      setStagedDocs(docs);
                                    }, 100);
                                  }}
                                  onDocumentRemove={() => {
                                    formRef.current?.handleDocumentRemove(idx);
                                    // Update local staged list
                                    setTimeout(() => {
                                      const docs = formRef.current?.getStagedDocuments?.() || [];
                                      setStagedDocs(docs);
                                    }, 100);
                                  }}
                                  documentType={d.documentType as DocumentType}
                                  isErrored={isErrored}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </TabsContent>
            </Tabs>
          </div>
          <div className="flex flex-row justify-between flex-shrink-0">
            <Button
              onClick={() => {
                setIsSubmitting(false);
                onClose(false);
              }}
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              variant="brand"
              className={isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
            >
              {isSubmitting ? "Uploading..." : "Finish"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Paywall would go here if needed */}
      {paywallOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Upgrade Required</h3>
            <p className="text-gray-600 mb-4">
              You've reached the free plan limit of {FREE_DOCUMENT_LIMIT} documents. Please upgrade
              to continue uploading.
            </p>
            <Button onClick={() => setPaywallOpen(false)}>Close</Button>
          </div>
        </div>
      )}
    </>
  );
}
